import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Cpu, MemoryStick, HardDrive, Server, Boxes, Globe,
  Archive, ScrollText, CheckCircle2, AlertTriangle, XCircle, Clock, ShieldCheck,
  ChevronRight, PieChart, FileWarning, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SecretsEnv from "../components/SecretsEnv";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const REFRESH_MS = 15000;

const fmtBytes = (n) => {
  if (!n) return "0 o";
  const units = ["o", "Ko", "Mo", "Go", "To"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const fmtDuration = (s) => {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d} j ${h} h` : `${h} h ${Math.floor((s % 3600) / 60)} min`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

/** Vert sous 70 %, ambre jusqu'à 90 %, rouge au-delà. */
const level = (pct) => (pct >= 90 ? "red" : pct >= 70 ? "amber" : "green");
const BAR = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
const TEXT = { green: "text-emerald-600", amber: "text-amber-600", red: "text-red-600" };

const Gauge = ({ icon: Icon, label, percent, detail }) => {
  const l = level(percent);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <span className={`ml-auto text-lg font-bold ${TEXT[l]}`}>{percent}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${BAR[l]}`}
             style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
      <p className="text-xs text-slate-500 mt-2">{detail}</p>
    </div>
  );
};

const StatusDot = ({ online }) => (
  <span className={`w-2 h-2 rounded-full shrink-0 ${online ? "bg-emerald-500" : "bg-slate-300"}`} />
);

/**
 * Barre de magnitude : une seule teinte, longueur proportionnelle à la valeur.
 * La couleur n'encode rien d'autre que « c'est une mesure » — le rang est déjà
 * porté par le tri et la longueur.
 */
const Bar = ({ label, size, max, muted, onClick, children }) => {
  const pct = max > 0 ? (size / max) * 100 : 0;
  return (
    <div className={`group ${onClick ? "cursor-pointer" : ""}`} onClick={onClick}>
      <div className="flex items-baseline gap-2 text-sm mb-1">
        {children}
        <span className={`truncate ${muted ? "text-slate-500" : "font-medium text-slate-800"}`}>
          {label}
        </span>
        <div className="flex-1 border-b border-dotted border-slate-200 min-w-[8px]" />
        <span className="text-slate-600 tabular-nums shrink-0">{fmtBytes(size)}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${muted ? "bg-indigo-200" : "bg-indigo-500"}`}
             style={{ width: `${Math.max(pct, 0.5)}%` }}
             title={`${label} — ${fmtBytes(size)}`} />
      </div>
    </div>
  );
};

/* Ces jetons pilotent l'hebergement, le DNS et les depots : ils ne dependent
   d'aucun projet, et vivent donc hors des .env de projet. */
const MODELES_INFRA = [
  { titre: "Hébergement et DNS", cles: ["HOSTINGER_API_TOKEN", "HETZNER_API_TOKEN"] },
  { titre: "OVH (DNS ma-grp.fr)", cles: ["OVH_APPLICATION_KEY", "OVH_APPLICATION_SECRET", "OVH_CONSUMER_KEY"] },
  { titre: "Dépôts", cles: ["GITHUB_TOKEN"] },
];

export default function InfrastructurePage() {
  const { token } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [apps, setApps] = useState(null);
  const [backups, setBackups] = useState(null);
  const [sources, setSources] = useState(null);
  const [logId, setLogId] = useState(null);
  const [logBody, setLogBody] = useState(null);
  const [storage, setStorage] = useState(null);
  const [openDir, setOpenDir] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const logRef = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };
  // Un objet neuf à chaque rendu : on le fige pour ne pas relancer les effets en boucle.
  const authRef = useRef(auth);
  authRef.current = auth;

  const load = useCallback(async (silent = false) => {
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const [o, a, b, l] = await Promise.all([
        axios.get(`${API_URL}/api/infra/overview`, authRef.current),
        axios.get(`${API_URL}/api/infra/apps`, authRef.current),
        axios.get(`${API_URL}/api/infra/backups`, authRef.current),
        axios.get(`${API_URL}/api/infra/logs`, authRef.current),
      ]);
      setOverview(o.data);
      setApps(a.data);
      setBackups(b.data);
      setSources(l.data.sources);
    } catch {
      if (!silent) toast.error("Impossible de lire l'état du serveur");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rafraîchissement auto : uniquement hors onglet Logs, pour ne pas écraser
  // la lecture en cours d'un fichier.
  useEffect(() => {
    if (tab === "logs") return undefined;
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [tab, load]);

  const loadStorage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/infra/storage`, authRef.current);
      setStorage(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { loadStorage(); }, [loadStorage]);

  // L'analyse tourne côté serveur (~1-2 min) : on interroge son avancement
  // uniquement tant qu'elle tourne, et on s'arrête dès qu'elle a fini.
  useEffect(() => {
    if (storage?.scan?.status !== "running") return undefined;
    const t = setInterval(async () => {
      const data = await loadStorage();
      if (data?.scan?.status !== "running") {
        clearInterval(t);
        toast.success("Analyse du disque terminée");
      }
    }, 5000);
    return () => clearInterval(t);
  }, [storage?.scan?.status, loadStorage]);

  const startScan = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/infra/storage/scan`, {}, authRef.current);
      if (!data.started) return toast.info(data.reason);
      toast.info("Analyse lancée — comptez une à deux minutes");
      loadStorage();
    } catch {
      toast.error("Impossible de lancer l'analyse");
    }
  }, [loadStorage]);

  const openLog = useCallback(async (id) => {
    setLogId(id);
    setLogBody(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/infra/logs/read`,
        { ...authRef.current, params: { id, lines: 400 } });
      setLogBody(data);
    } catch {
      toast.error("Lecture du log impossible");
      setLogBody({ content: "" });
    }
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logBody]);

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </CRMLayout>
    );
  }

  const mem = overview?.memory;
  const rootDisk = overview?.disks?.find((d) => d.mount === "/") || overview?.disks?.[0];
  const services = apps?.services || [];
  const expiring = (apps?.nginx || []).filter(
    (n) => n.cert_days_left !== null && n.cert_days_left < 21);

  const logGroups = (sources || []).reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Infrastructure</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {overview?.hostname} · {overview?.cores} cœurs · en ligne depuis{" "}
              {fmtDuration(overview?.uptime_seconds || 0)}
            </p>
          </div>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => load()} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="apps">Applications ({services.length})</TabsTrigger>
            <TabsTrigger value="storage">Disque</TabsTrigger>
            <TabsTrigger value="backups">Sauvegardes ({backups?.totals?.jobs || 0})</TabsTrigger>
            <TabsTrigger value="logs">Logs ({sources?.length || 0})</TabsTrigger>
          </TabsList>

          {/* ---------- VUE D'ENSEMBLE ---------- */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Gauge icon={Cpu} label="Processeur" percent={overview?.cpu_percent ?? 0}
                     detail={`Charge ${overview?.load?.map((l) => l.toFixed(2)).join(" · ")} sur ${overview?.cores} cœurs`} />
              <Gauge icon={MemoryStick} label="Mémoire" percent={mem?.percent ?? 0}
                     detail={`${fmtBytes(mem?.used)} utilisés sur ${fmtBytes(mem?.total)} · swap ${mem?.swap_percent}%`} />
              <Gauge icon={HardDrive} label="Disque" percent={rootDisk?.percent ?? 0}
                     detail={`${fmtBytes(rootDisk?.used)} sur ${fmtBytes(rootDisk?.total)} · ${fmtBytes(rootDisk?.free)} libres`} />
            </div>

            {(rootDisk?.percent >= 85 || mem?.percent >= 90 || expiring.length > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1.5">
                <p className="flex items-center gap-2 font-semibold text-amber-900 text-sm">
                  <AlertTriangle className="w-4 h-4" /> À surveiller
                </p>
                {rootDisk?.percent >= 85 && (
                  <p className="text-sm text-amber-800">
                    Disque {rootDisk.mount} rempli à {rootDisk.percent}% — {fmtBytes(rootDisk.free)} restants.
                  </p>
                )}
                {mem?.percent >= 90 && (
                  <p className="text-sm text-amber-800">Mémoire à {mem.percent}%.</p>
                )}
                {expiring.map((n) => (
                  <p key={n.id} className="text-sm text-amber-800">
                    Certificat {n.domains[0] || n.name} expire dans {n.cert_days_left} jours.
                  </p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Server, label: "Services en ligne", value: `${apps?.totals?.online}/${apps?.totals?.services}` },
                { icon: MemoryStick, label: "RAM des apps", value: fmtBytes(apps?.totals?.memory) },
                { icon: Globe, label: "Sites nginx", value: apps?.nginx?.length || 0 },
                { icon: Archive, label: "Sauvegardes", value: fmtBytes(backups?.storage?.total_size) },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5">
                  <s.icon className="w-4 h-4 text-slate-400 mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h2 className="font-semibold text-slate-900 mb-4">Disques</h2>
              <div className="space-y-4">
                {overview?.disks?.map((d) => (
                  <div key={d.mount}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-medium text-slate-700">{d.mount}</span>
                      <span className="text-slate-500">
                        {fmtBytes(d.used)} / {fmtBytes(d.total)} ({d.percent}%)
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${BAR[level(d.percent)]}`} style={{ width: `${d.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ---------- APPLICATIONS ---------- */}
          <TabsContent value="apps" className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                <Boxes className="w-4 h-4 text-slate-400" />
                <h2 className="font-semibold text-slate-900">Services déployés</h2>
                <span className="text-xs text-slate-500">
                  {apps?.totals?.pm2} PM2 · {apps?.totals?.docker} Docker
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left font-medium px-5 py-2.5">Service</th>
                      <th className="text-left font-medium px-5 py-2.5">Type</th>
                      <th className="text-right font-medium px-5 py-2.5">Mémoire</th>
                      <th className="text-right font-medium px-5 py-2.5">CPU</th>
                      <th className="text-left font-medium px-5 py-2.5">Détail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...services].sort((a, b) => b.memory - a.memory).map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <StatusDot online={s.status === "online"} />
                            <span className="font-medium text-slate-900">{s.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-slate-500">{s.kind}</td>
                        <td className="px-5 py-3 text-right font-medium text-slate-700">
                          {fmtBytes(s.memory)}
                        </td>
                        <td className="px-5 py-3 text-right text-slate-500">
                          {(s.cpu ?? 0).toFixed(1)}%
                        </td>
                        <td className="px-5 py-3 text-slate-400 text-xs truncate max-w-xs">
                          {s.kind === "pm2"
                            ? `${s.path}${s.restarts ? ` · ${s.restarts} redémarrages` : ""}`
                            : s.status_detail}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-400" />
                <h2 className="font-semibold text-slate-900">Sites nginx</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {apps?.nginx?.map((n) => (
                  <div key={n.id} className="px-5 py-3 flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 text-sm truncate">
                        {n.domains.length ? n.domains.join(", ") : n.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {n.upstreams.join(", ") || "—"}
                      </p>
                    </div>
                    {n.ssl ? (
                      <span className={`flex items-center gap-1 text-xs ${
                        n.cert_days_left < 21 ? "text-amber-600" : "text-emerald-600"}`}>
                        <ShieldCheck className="w-3.5 h-3.5" />
                        {n.cert_days_left !== null ? `${n.cert_days_left} j` : "HTTPS"}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">HTTP</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ---------- DISQUE ---------- */}
          <TabsContent value="storage" className="space-y-6">
            {(() => {
              const scanning = storage?.scan?.status === "running";
              const snap = storage?.snapshot;
              const live = (storage?.disks || []).find((d) => d.mount === "/") || rootDisk;
              const reclaimable = (snap?.docker || []).reduce((n, d) => n + d.reclaimable, 0);
              // Toutes les barres d'un même bloc partagent l'échelle du plus gros poste,
              // sinon les longueurs ne seraient plus comparables entre elles.
              const rootsMax = Math.max(
                ...(snap?.roots || []).map((r) => r.size), snap?.unaccounted || 0, 1);

              return (
                <>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[240px]">
                      <Gauge icon={HardDrive} label={`Disque ${live?.mount || "/"}`}
                             percent={live?.percent ?? 0}
                             detail={`${fmtBytes(live?.used)} sur ${fmtBytes(live?.total)} · ${fmtBytes(live?.free)} libres`} />
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 min-w-[220px]">
                      <Trash2 className="w-4 h-4 text-slate-400 mb-2" />
                      <p className="text-2xl font-bold text-slate-900">{fmtBytes(reclaimable)}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Récupérable via Docker</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 min-w-[220px]">
                      <Clock className="w-4 h-4 text-slate-400 mb-2" />
                      <p className="text-sm font-semibold text-slate-900">
                        {snap ? fmtDate(snap.scanned_at) : "Jamais analysé"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 mb-2">Dernière analyse</p>
                      <Button size="sm" variant="outline" onClick={startScan} disabled={scanning}>
                        {scanning
                          ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {storage.scan.step || "analyse…"}</>
                          : <><PieChart className="w-3.5 h-3.5 mr-1.5" /> Analyser le disque</>}
                      </Button>
                    </div>
                  </div>

                  {!snap ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                      <PieChart className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Aucune analyse disponible</p>
                      <p className="text-sm text-slate-400 mt-1 mb-4">
                        L'analyse parcourt tout le disque et prend une à deux minutes.
                      </p>
                      <Button onClick={startScan} disabled={scanning}>
                        {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Lancer l'analyse
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <h2 className="font-semibold text-slate-900 mb-1">Répartition du disque</h2>
                        <p className="text-xs text-slate-500 mb-4">
                          Cliquez sur un répertoire pour voir ce qu'il contient.
                        </p>
                        <div className="space-y-3">
                          {snap.roots.map((r) => (
                            <div key={r.path}>
                              <Bar label={r.path} size={r.size} max={rootsMax}
                                   onClick={() => setOpenDir(openDir === r.path ? null : r.path)}>
                                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                                  openDir === r.path ? "rotate-90" : ""}`} />
                              </Bar>
                              {openDir === r.path && (
                                <div className="mt-3 ml-5 pl-4 border-l border-slate-200 space-y-2.5">
                                  {r.children.map((c) => (
                                    <div key={c.path}>
                                      <Bar label={c.name} size={c.size} max={r.children[0]?.size || 1} />
                                      {c.children?.length > 0 && (
                                        <div className="ml-4 mt-1.5 space-y-1">
                                          {c.children.map((g) => (
                                            <div key={g.path}
                                                 className="flex items-baseline gap-2 text-xs text-slate-500">
                                              <span className="truncate">{g.name}</span>
                                              <div className="flex-1 border-b border-dotted border-slate-100" />
                                              <span className="tabular-nums shrink-0">{fmtBytes(g.size)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          <Bar label="Non attribué (système, fichiers ouverts, swap…)"
                               size={snap.unaccounted} max={rootsMax} muted />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Boxes className="w-4 h-4 text-slate-400" /> Docker
                          </h2>
                          <table className="w-full text-sm">
                            <thead className="text-xs uppercase text-slate-400">
                              <tr>
                                <th className="text-left font-medium pb-2">Type</th>
                                <th className="text-right font-medium pb-2">Taille</th>
                                <th className="text-right font-medium pb-2">Récupérable</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {snap.docker.map((d) => (
                                <tr key={d.type}>
                                  <td className="py-2 text-slate-700">
                                    {d.type}
                                    <span className="text-slate-400 text-xs ml-1.5">
                                      {d.active}/{d.total} actifs
                                    </span>
                                  </td>
                                  <td className="py-2 text-right tabular-nums text-slate-700">
                                    {fmtBytes(d.size)}
                                  </td>
                                  <td className={`py-2 text-right tabular-nums ${
                                    d.reclaimable > 0 ? "text-amber-600 font-medium" : "text-slate-300"}`}>
                                    {d.reclaimable > 0 ? fmtBytes(d.reclaimable) : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                            <ScrollText className="w-4 h-4 text-slate-400" /> Logs des conteneurs
                          </h2>
                          <p className="text-xs text-slate-500 mb-4">
                            Poste invisible dans <code>docker system df</code>, et qui grossit sans limite
                            sans règle de rotation.
                          </p>
                          <div className="space-y-2">
                            {snap.container_logs.slice(0, 8).map((l) => (
                              <div key={l.name} className="flex items-baseline gap-2 text-sm">
                                <span className={`truncate ${l.orphan ? "text-slate-400 italic" : "text-slate-700"}`}>
                                  {l.name}
                                </span>
                                <div className="flex-1 border-b border-dotted border-slate-200" />
                                <span className={`tabular-nums shrink-0 ${
                                  l.size > 500 * 1024 ** 2 ? "text-red-600 font-semibold" : "text-slate-600"}`}>
                                  {fmtBytes(l.size)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                          <FileWarning className="w-4 h-4 text-slate-400" /> Plus gros fichiers (&gt; 200 Mo)
                        </h2>
                        <p className="text-xs text-slate-500 mb-4">
                          C'est ici que se cachent les dumps oubliés et les logs qui ont dérapé.
                        </p>
                        <div className="space-y-1.5">
                          {snap.largest_files.map((f) => (
                            <div key={f.path} className="flex items-baseline gap-3 text-sm">
                              <span className="tabular-nums text-slate-900 font-medium w-20 shrink-0">
                                {fmtBytes(f.size)}
                              </span>
                              <span className="text-slate-500 text-xs truncate font-mono" title={f.path}>
                                {f.path}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </TabsContent>

          {/* ---------- SAUVEGARDES ---------- */}
          <TabsContent value="backups" className="space-y-6">
            {backups?.totals?.running > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                <p className="text-sm text-indigo-900 font-medium">
                  {backups.totals.running} sauvegarde(s) en cours d'exécution.
                </p>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                <Archive className="w-4 h-4 text-slate-400" />
                <h2 className="font-semibold text-slate-900">Tâches de sauvegarde planifiées</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {backups?.jobs?.length ? backups.jobs.map((j, i) => (
                  <div key={i} className="px-5 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      {j.running ? <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                        : j.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        : j.status === "failed" ? <XCircle className="w-4 h-4 text-red-500" />
                        : <Clock className="w-4 h-4 text-slate-300" />}
                      <span className="font-medium text-slate-900 text-sm">{j.name}</span>
                      <code className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {j.schedule}
                      </code>
                      <span className="text-xs text-slate-400">{j.source}</span>
                      <div className="flex-1" />
                      <span className="text-xs text-slate-500">
                        {j.running ? "en cours"
                          : j.last_run ? `dernière : ${fmtDate(j.last_run)}`
                          : "aucun log"}
                      </span>
                    </div>
                    {j.tail && (
                      <pre className="mt-2 text-[11px] leading-relaxed bg-slate-900 text-slate-300 rounded-lg p-3 overflow-x-auto max-h-32">
                        {j.tail}
                      </pre>
                    )}
                  </div>
                )) : (
                  <p className="px-5 py-8 text-center text-sm text-slate-400">
                    Aucune tâche de sauvegarde détectée dans cron.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">
                  Stockage · {fmtBytes(backups?.storage?.total_size)}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {backups?.storage?.dirs?.map((d) => `${d.path} (${fmtBytes(d.size)})`).join(" · ")}
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {backups?.storage?.recent?.map((a) => (
                  <div key={`${a.dir}/${a.name}`} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                    <span className="text-slate-700 truncate flex-1">{a.name}</span>
                    <span className="text-slate-400 text-xs">{fmtBytes(a.size)}</span>
                    <span className="text-slate-400 text-xs w-32 text-right">{fmtDate(a.modified_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ---------- LOGS ---------- */}
          <TabsContent value="logs">
            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden max-h-[70vh] overflow-y-auto">
                {Object.entries(logGroups).map(([group, items]) => (
                  <div key={group}>
                    <p className="px-4 py-2 text-xs font-semibold uppercase text-slate-400 bg-slate-50 sticky top-0">
                      {group}
                    </p>
                    {items.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => openLog(s.id)}
                        className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                          logId === s.id ? "bg-indigo-50 text-indigo-900 font-medium" : "text-slate-700"}`}
                      >
                        <span className="block truncate">{s.label}</span>
                        {s.size != null && (
                          <span className="block text-xs text-slate-400">{fmtBytes(s.size)}</span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>

              <div className="bg-slate-900 rounded-xl overflow-hidden flex flex-col max-h-[70vh]">
                {!logId ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-500 py-24">
                    <ScrollText className="w-8 h-8 mb-2" />
                    <p className="text-sm">Choisissez un log à consulter</p>
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-200 truncate">
                        {logBody?.label || "Chargement…"}
                      </span>
                      <span className="text-xs text-slate-500 truncate">{logBody?.path}</span>
                      <div className="flex-1" />
                      <Button size="sm" variant="ghost"
                              className="text-slate-400 hover:text-white hover:bg-slate-800 h-7"
                              onClick={() => openLog(logId)}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <pre ref={logRef}
                         className="flex-1 overflow-auto p-4 text-[12px] leading-relaxed text-slate-300 font-mono whitespace-pre-wrap break-all">
                      {logBody ? (logBody.content || "(vide)") : "Chargement…"}
                    </pre>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <div className="mt-6">
        <SecretsEnv
          route={`${API_URL}/api/infra/secrets`}
          token={token}
          titre="Secrets d'infrastructure"
          modeles={MODELES_INFRA}
          avecRedemarrage={false}
          aide="Jetons qui pilotent l'hébergement, le DNS et les dépôts. Ils ne dépendent d'aucun projet et sont conservés hors des .env, en droits 600."
        />
      </div>

    </CRMLayout>
  );
}
